/**
 * @file `usePageSize` — derive a list's page size *and* an exact row height
 *       from its container's height, so a paged panel fills the viewport
 *       with no scroll box and no dead gap above the pager.
 * @module @labcraft/admin/ui/hooks/use-page-size
 *
 * Panel-agnostic: any numbered-pager panel measures its scroll container
 * with a `ResizeObserver` and fetches exactly the number of rows that fit
 * (`floor(available / base)`), recomputing on resize. When the container
 * holds a `<thead>` (the common table case) its height is subtracted so the
 * count reflects the body area only.
 *
 * Two numbers come back. `pageSize` is the row count. `rowHeight` is the
 * exact per-row height (`available / pageSize`) that makes those rows
 * *consume the whole body area* — the sub-row remainder `floor` would leave
 * is distributed back into the rows, so there is no gap between the last row
 * and the pager. The caller applies `rowHeight` as a scoped `--row-h`
 * override on the container.
 *
 * The `base` row height that drives the *count* is read from the `--row-h`
 * design token at the document root (`:root`) — never from the container,
 * because the caller overrides `--row-h` there with the stretched value and
 * reading it back would feed the stretch into the next count (an oscillation
 * that settles a row too short). Reading the token (vs measuring a rendered
 * row) also sidesteps the empty-first-render chicken-and-egg: the token
 * resolves before any row exists, so the first page size is right.
 *
 * Returns `pageSize: 0` until the first measure (before layout is known);
 * callers gate their query on `pageSize > 0` so no `limit=0` request issues.
 */

import { useLayoutEffect, useState, type RefObject } from 'react'

/** Options for {@link usePageSize}. */
export type UsePageSizeOptions = {
  rowHeight?: number
  min?: number
  max?: number
}

/**
 * The measured page geometry: how many rows fit, and how tall each must be
 * to fill the body area exactly.
 *
 * @property pageSize - Fittable row count (0 before the first measure).
 * @property rowHeight - Per-row height in px that fills the body with no
 *   gap; 0 before the first measure.
 */
export type PageSizing = {
  pageSize: number
  rowHeight: number
}

/**
 * Measure `ref`'s client height and return the row count that fits (minus
 * any `<thead>`), clamped to `[min, max]`, plus the exact per-row height
 * that fills the body area. Recomputed on resize.
 *
 * @param ref - The scroll container whose height bounds the page.
 * @param options - `rowHeight` fallback (default 44, used only when the
 *   `--row-h` token is unset), and `min` / `max` clamps (default 1 / 200,
 *   the API's page-size ceiling).
 * @returns The `{ pageSize, rowHeight }` geometry, both 0 before first measure.
 */
export function usePageSize(
  ref: RefObject<HTMLElement | null>,
  options: UsePageSizeOptions = {},
): PageSizing {
  const { rowHeight = 44, min = 1, max = 200 } = options
  const [sizing, setSizing] = useState<PageSizing>({
    pageSize: 0,
    rowHeight: 0,
  })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const measure = (): void => {
      const head = el.querySelector('thead')
      const reserve = head instanceof HTMLElement ? head.offsetHeight : 0
      const available = el.clientHeight - reserve
      const base = rootRowHeight(el, rowHeight)
      const fit = Math.floor(available / base)
      const pageSize = Math.max(min, Math.min(max, fit))
      // Stretch the rows to consume the whole body area: no sub-row gap.
      // Before the first real layout `available` can be <= 0; fall back to
      // the base so we never emit a zero/negative row height.
      const filled = available > 0 ? available / pageSize : base
      setSizing(prev =>
        prev.pageSize === pageSize && prev.rowHeight === filled
          ? prev
          : { pageSize, rowHeight: filled },
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [ref, rowHeight, min, max])

  return sizing
}

/**
 * The base per-row height in px, read from the `--row-h` design token at the
 * document root — the token's canonical home, which a per-panel `--row-h`
 * override on the container never touches (so the stretch can't feed back
 * into the count). Falls back to `fallback` when the token is unset.
 *
 * @param el - Any element in the target document (used only for its root).
 * @param fallback - Height to use when `--row-h` doesn't resolve.
 * @returns The base row height in px.
 */
function rootRowHeight(el: HTMLElement, fallback: number): number {
  const root = el.ownerDocument.documentElement
  const raw = getComputedStyle(root).getPropertyValue('--row-h').trim()
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}
